import { FC, useState } from 'react'
import {
  MdArchive,
  MdContentCopy,
  MdOpenInNew,
  MdOutlineArchive,
  MdOutlineStarBorder,
  MdOutlineStarPurple500,
} from 'react-icons/md'
import { useTranslation } from 'react-i18next'
import { Link, useNavigate } from 'react-router-dom'
import Config, { priceForLanguage } from '../../../lib/Config'
import { useFormatCurrency } from '../../../lib/CurrencyFormatter'
import { Budget } from '../../../lib/Types'
import Headline from '../../Elements/Headline'
import Text from '../../Elements/Text'
import Button from '../../Formik/Button'
import Paywall from '../../Paywall'
import TrialLabel from '../TrialLabel'
import { useBudgetSums, useCloneBudget } from '../lib/Hooks/Budget'
import { useUpdateSheet } from '../lib/Hooks/Sheet'
import {
  isFreeBudgetInPremiumPeriod,
  isOnActiveTrial,
  isPaymentOverdue,
} from '../lib/Payment'
import BudgetDevelopmentChart from './BudgetDevelopmentChart'
interface Props {
  budget: Budget
}

const Card: FC<Props> = ({ budget }) => {
  const { t, i18n } = useTranslation(['budget', 'common'])
  const formatCurrency = useFormatCurrency()
  const { data: budgetSums } = useBudgetSums(budget)
  const { mutate: update } = useUpdateSheet()
  const { mutate: cloneBudget, isPending: isCloning } = useCloneBudget()
  const navigate = useNavigate()
  const [showPaywall, togglePaywall] = useState(false)
  const overdue = isPaymentOverdue(budget)
  const onActiveTrial = isOnActiveTrial(budget)
  const freeInPremiumPeriod = isFreeBudgetInPremiumPeriod(budget)
  const price = priceForLanguage(Config.PRICE_BUDGET, i18n.language)
  const budgetName =
    budget.name || t('budget:card.defaultName', { id: budget.id })
  const purchaseName = budget.name || t('common:paywall.budget')

  const toggleFavorite = () => {
    const newBudget = structuredClone(budget)

    newBudget.is_favorite = !newBudget.is_favorite

    update(newBudget)
  }

  const toggleArchive = () => {
    const newBudget = structuredClone(budget)

    newBudget.is_archived = !newBudget.is_archived

    update(newBudget)
  }

  const handleClone = () => {
    const name =
      budget.name || t('budget:card.defaultName', { id: budget.id })

    if (!window.confirm(t('budget:card.confirmClone', { name }))) {
      return
    }

    cloneBudget(budget, {
      onSuccess: (newBudget) => navigate(`/budget/${newBudget.id}/`),
    })
  }

  return (
    <div
      className={`${overdue ? 'bg-trial-ended' : onActiveTrial ? 'bg-active-trial' : 'bg-gray-100'} p-4 mt-4`}
    >
      <Link to={`/budget/${budget.id}`}>
        <div className='flex justify-between items-start'>
          <Headline size='base' className='mb-4'>
            <strong>{budgetName}</strong>
          </Headline>
          {overdue
            ? (
              <div
                onClick={(e) => {
                  e.preventDefault()
                  e.stopPropagation()
                  togglePaywall(true)
                }}
              >
                <Button theme='gray'>
                  {t('common:paywall.pay', { price })}
                </Button>
              </div>
            )
            : (
              <Button theme='gray'>
                <div className='flex'>
                  <MdOpenInNew size='1.25rem' className='mr-2' />
                  {t('budget:card.open')}
                </div>
              </Button>
            )}
        </div>
        <Text size='base'>{t('budget:card.totalExpenses')}</Text>
        <Headline size='5xl' className='mb-2'>
          {formatCurrency(budgetSums?.expense.at(-1)?.amount || 0, false)}
        </Headline>

        <div className='mt-6'>
          <BudgetDevelopmentChart budget={budget} showEmptyWarning={false} />
        </div>
      </Link>
      <div className='flex justify-between items-center mt-2'>
        <TrialLabel
          sheet={budget}
          overdue={overdue}
          onActiveTrial={onActiveTrial}
          isPaid={budget.is_paid || budget.has_accepted_payment}
          isFree={!!budget.is_free}
          freeInPremiumPeriod={freeInPremiumPeriod}
        />
        <div className='flex items-center ml-auto'>
          <div onClick={toggleFavorite} className='cursor-pointer'>
            {!budget.is_favorite && (
              <MdOutlineStarBorder
                size='1.5rem'
                className='mr-2'
                title={t('budget:card.markFavorite')}
              />
            )}
            {budget.is_favorite && (
              <MdOutlineStarPurple500
                size='1.5rem'
                className='mr-2'
                title={t('budget:card.unmarkFavorite')}
              />
            )}
          </div>
          {!budget.is_archived && (
            <MdOutlineArchive
              size='1.5rem'
              className='cursor-pointer mr-2'
              onClick={toggleArchive}
              title={t('budget:card.archive')}
            />
          )}
          {budget.is_archived && (
            <MdArchive
              size='1.5rem'
              className='cursor-pointer mr-2'
              onClick={toggleArchive}
              title={t('budget:card.restore')}
            />
          )}
          {isCloning
            ? (
              <div role='status'>
                <svg
                  aria-hidden='true'
                  className='inline w-[1.35rem] h-[1.35rem] text-gray-300 animate-spin fill-gray-600'
                  viewBox='0 0 100 101'
                  fill='none'
                  xmlns='http://www.w3.org/2000/svg'
                >
                  <path
                    d='M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z'
                    fill='currentColor'
                  />
                  <path
                    d='M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z'
                    fill='currentFill'
                  />
                </svg>
                <span className='sr-only'>{t('budget:card.cloning')}</span>
              </div>
            )
            : (
              <MdContentCopy
                size='1.35rem'
                className='cursor-pointer'
                onClick={handleClone}
                title={t('budget:card.cloneTitle')}
              />
            )}
        </div>
      </div>
      <Paywall
        show={showPaywall}
        onHide={() => togglePaywall(false)}
        sheetType={budget.sheet_type}
        sheetId={budget.id}
        name={purchaseName}
      />
    </div>
  )
}

export default Card
